import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Container, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTeacherAuth } from '../hooks/useTeacherAuth';
import { teacherPortalAPI } from '../utils/api';
import LanguageSelector from '../components/LanguageSelector';
import LittleLensLogo from '../components/LittleLensLogo';

const TeacherChangePassword: React.FC = () => {
  const { t } = useTranslation('auth', { keyPrefix: 'portalChangePassword' });
  const navigate = useNavigate();
  const { markPasswordChanged } = useTeacherAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', new_password_confirmation: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    if (form.new_password !== form.new_password_confirmation) {
      setError(t('passwordsDoNotMatch'));
      return;
    }
    setLoading(true);
    try {
      const response = await teacherPortalAPI.changePassword(form);
      setMessage(response.message || t('passwordUpdated'));
      setForm({ current_password: '', new_password: '', new_password_confirmation: '' });
      markPasswordChanged();
      navigate('/teacher/dashboard', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('changeFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', p: 2 }}>
      <Box sx={{ position: 'absolute', top: 16, right: 16, color: 'text.primary' }}>
        <LanguageSelector />
      </Box>
      <Container maxWidth="sm">
        <Card elevation={16} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <LittleLensLogo maxWidth={90} />
            </Box>
            <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>{t('title')}</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>{t('subtitle')}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
            <form onSubmit={submit}>
              <TextField fullWidth required type="password" label={t('currentPassword')} margin="normal" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
              <TextField fullWidth required type="password" label={t('newPassword')} margin="normal" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
              <TextField fullWidth required type="password" label={t('confirmNewPassword')} margin="normal" value={form.new_password_confirmation} onChange={(e) => setForm({ ...form, new_password_confirmation: e.target.value })} />
              <Button fullWidth type="submit" variant="contained" size="large" disabled={loading} sx={{ mt: 3, py: 1.4, bgcolor: 'primary.main' }}>{loading ? <CircularProgress size={22} color="inherit" /> : t('updatePassword')}</Button>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default TeacherChangePassword;
